import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const jwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  const token = auth.getToken();

  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('/auth/')) {
        return handleRefresh(req, next, auth);
      }
      if (error.status === 401 && req.url.includes('/auth/refresh-token')) {
        auth.clearSession();
        return throwError(() => error);
      }
      return throwError(() => error);
    })
  );
};

function handleRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshSubject.next(null);
    return auth.refreshToken().pipe(
      switchMap(res => {
        isRefreshing = false;
        const newToken = res.data.accessToken;
        refreshSubject.next(newToken);
        const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
        return next(retryReq);
      }),
      catchError(err => {
        isRefreshing = false;
        auth.clearSession();
        return throwError(() => err);
      })
    );
  }
  return refreshSubject.pipe(
    filter(t => t !== null),
    take(1),
    switchMap(token => {
      const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${token as string}` } });
      return next(retryReq);
    })
  );
}
